/**
 * Phase 3b: Geometry Utilities
 * Vector math and line intersection for raycasting
 */

/**
 * A 2D point
 */
export interface Point {
  x: number
  y: number
}

/**
 * A line segment defined by two points
 */
export interface LineSegment {
  start: Point
  end: Point
}

/**
 * The canvas viewport transform. PixiJS applies this to the stage:
 * `screen = world * scale + {x, y}`. Pointer events report positions
 * in screen space (`event.global`), so converting them into world
 * space requires the inverse: `world = (screen - {x, y}) / scale`.
 */
export interface Viewport {
  /** Stage x translation in screen pixels */
  x: number
  /** Stage y translation in screen pixels */
  y: number
  /** Stage scale (zoom) */
  scale: number
}

/**
 * An axis-aligned rectangle. Not assumed to be normalized (x1/y1 may
 * be greater than x2/y2); helpers normalize as needed.
 */
export interface Rect {
  x1: number
  y1: number
  x2: number
  y2: number
}

/**
 * Convert a screen-space point (e.g. a PixiJS `event.global`) into
 * world space by applying the inverse viewport transform.
 *
 * This is the same formula the ruler/drag-spawn paths use inline:
 * `(localX - v.x) / v.scale`. Centralized here so the marquee box and
 * its hit-test stay in lockstep with the cursor at any pan/zoom.
 */
export function screenToWorld(screen: Point, viewport: Viewport): Point {
  return {
    x: (screen.x - viewport.x) / viewport.scale,
    y: (screen.y - viewport.y) / viewport.scale,
  }
}

/**
 * Convert a native pointer's CLIENT coordinate (e.g. `PointerEvent.clientX`)
 * into PixiJS SCREEN space — the same space PixiJS reports in `event.global`
 * and hit-tests against via `renderer.events.rootBoundary.hitTest`.
 *
 * This MUST match what PixiJS does internally. In @pixi/events 7.x,
 * `EventSystem.mapPositionToPoint` computes:
 *
 *   global = (client - rect.left) * (canvas.width / rect.width) / resolution
 *
 * where `canvas.width` is the backing-store size in device px
 * (`renderWidth * resolution` under `autoDensity`) and `rect.width` is the
 * canvas's CSS display width. Substituting `canvas.width = renderWidth * res`,
 * the two resolution factors cancel and this reduces to:
 *
 *   global = (client - rect.left) * (renderWidth / rect.width)
 *
 * i.e. the CSS→render scale. On desktop the canvas is displayed 1:1
 * (`renderWidth === rect.width`) so the factor is exactly 1 and this is a
 * no-op — the working desktop path is unchanged. On mobile, where the canvas's
 * CSS display size can diverge from its logical render size, the factor
 * corrects the coordinate so non-Pixi handlers land in the same world cell as
 * Pixi's own `event.global`. Without it, raw `client - rect.left` drifts
 * further from the true hit point the farther the pointer is from the origin —
 * the "clicking a token in part of the canvas does nothing" bug.
 *
 * `resolution` is the renderer's resolution (`app.renderer.resolution`), used
 * to divide the device-pixel scaling back out exactly as PixiJS does. `rect`
 * may be supplied to reuse a `getBoundingClientRect()` the caller already has.
 */
export function clientToScreen(
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
  resolution: number,
  rect?: DOMRect,
): Point {
  const r = rect ?? canvas.getBoundingClientRect()
  // Guard against a not-yet-laid-out canvas (zero-width rect / resolution):
  // fall back to a plain 1:1 client→canvas mapping rather than dividing by zero.
  if (r.width <= 0 || r.height <= 0 || resolution <= 0) {
    return { x: clientX - r.left, y: clientY - r.top }
  }
  return {
    x: (clientX - r.left) * ((canvas.width / r.width) / resolution),
    y: (clientY - r.top) * ((canvas.height / r.height) / resolution),
  }
}

/**
 * A token's world-space footprint, expressed in grid units. Width and
 * height are in grid cells; pixel extent is `(x|y) * gridSize` through
 * `(x|y + width|height) * gridSize`.
 */
export interface MarqueeToken {
  id: string
  /** Grid x of the token's top-left cell */
  x: number
  /** Grid y of the token's top-left cell */
  y: number
  /** Width in grid cells */
  width: number
  /** Height in grid cells */
  height: number
}

/**
 * Resolve which tokens fall inside a marquee (rubber-band) selection.
 *
 * The marquee rectangle is supplied in WORLD space (already passed
 * through {@link screenToWorld}); token positions are in grid space and
 * scaled to world pixels by `gridSize`. Standard AABB overlap test.
 *
 * Keeping this pure and world-space-only is what makes the selection
 * track the cursor regardless of pan/zoom — the previous bug compared a
 * screen-space rectangle against world-space tokens.
 */
export function marqueeHitTest(
  rectWorld: Rect,
  tokens: MarqueeToken[],
  gridSize: number
): string[] {
  const x1 = Math.min(rectWorld.x1, rectWorld.x2)
  const y1 = Math.min(rectWorld.y1, rectWorld.y2)
  const x2 = Math.max(rectWorld.x1, rectWorld.x2)
  const y2 = Math.max(rectWorld.y1, rectWorld.y2)

  const hits: string[] = []
  for (const t of tokens) {
    const tX1 = t.x * gridSize
    const tY1 = t.y * gridSize
    const tX2 = tX1 + gridSize * t.width
    const tY2 = tY1 + gridSize * t.height
    if (tX1 < x2 && tX2 > x1 && tY1 < y2 && tY2 > y1) {
      hits.push(t.id)
    }
  }
  return hits
}

/**
 * Calculate distance between two points
 */
export function distance(a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Calculate angle in radians from point a to point b
 */
export function angleBetween(a: Point, b: Point): number {
  return Math.atan2(b.y - a.y, b.x - a.x)
}

/**
 * Check if two line segments intersect and return intersection point
 * Uses parametric line intersection algorithm
 *
 * @param seg1 - First line segment
 * @param seg2 - Second line segment
 * @returns Intersection point if segments intersect, null otherwise
 */
export function lineSegmentIntersection(
  seg1: LineSegment,
  seg2: LineSegment
): Point | null {
  const { start: p1, end: p2 } = seg1
  const { start: p3, end: p4 } = seg2

  const x1 = p1.x
  const y1 = p1.y
  const x2 = p2.x
  const y2 = p2.y
  const x3 = p3.x
  const y3 = p3.y
  const x4 = p4.x
  const y4 = p4.y

  // Calculate denominators
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)

  // Lines are parallel (no intersection)
  if (Math.abs(denom) < 0.0001) {
    return null
  }

  // Calculate t and u parameters
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom

  // Check if intersection is within both line segments
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    // Calculate intersection point
    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1),
    }
  }

  return null
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 *
 * @param point - Point to test
 * @param polygon - Array of points defining the polygon
 * @returns true if point is inside polygon
 */
export function pointInPolygon(point: Point, polygon: Point[]): boolean {
  if (polygon.length < 3) return false

  let inside = false
  const x = point.x
  const y = point.y

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x
    const yi = polygon[i].y
    const xj = polygon[j].x
    const yj = polygon[j].y

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi

    if (intersect) inside = !inside
  }

  return inside
}

/**
 * Calculate the center point of a polygon
 */
export function polygonCenter(polygon: Point[]): Point {
  if (polygon.length === 0) {
    return { x: 0, y: 0 }
  }

  let sumX = 0
  let sumY = 0

  for (const point of polygon) {
    sumX += point.x
    sumY += point.y
  }

  return {
    x: sumX / polygon.length,
    y: sumY / polygon.length,
  }
}

/**
 * Normalize a vector (make it length 1)
 */
export function normalize(vector: Point): Point {
  const len = Math.sqrt(vector.x * vector.x + vector.y * vector.y)
  if (len === 0) return { x: 0, y: 0 }
  return {
    x: vector.x / len,
    y: vector.y / len,
  }
}

/**
 * Scale a vector by a scalar
 */
export function scale(vector: Point, scalar: number): Point {
  return {
    x: vector.x * scalar,
    y: vector.y * scalar,
  }
}

/**
 * Add two vectors
 */
export function add(a: Point, b: Point): Point {
  return {
    x: a.x + b.x,
    y: a.y + b.y,
  }
}

/**
 * Subtract two vectors (a - b)
 */
export function subtract(a: Point, b: Point): Point {
  return {
    x: a.x - b.x,
    y: a.y - b.y,
  }
}

/* -------------------------------------------------------------------------
 * Measurement-template geometry (ruler shapes: circle / square / cone)
 *
 * All helpers here are PURE and deterministic — they take the drag origin
 * (`start`), the live endpoint (`end`), and `gridSize` where a feet label
 * is needed, and return the render geometry plus a measurement label. No
 * DOM, no React, no viewport: callers apply the world->screen transform.
 *
 * Feet scale: the existing ruler assumes the D&D 5e convention of 5 ft per
 * grid square (see computeRulerDistance). We reuse that here: a pixel
 * length is converted to feet as `(px / gridSize) * FEET_PER_SQUARE`.
 * ---------------------------------------------------------------------- */

/** D&D 5e: one grid square is 5 feet. Matches computeRulerDistance. */
export const FEET_PER_SQUARE = 5

/** Convert a pixel length to feet using the 5 ft / square convention. */
export function pixelsToFeet(px: number, gridSize: number): number {
  if (gridSize <= 0) return 0
  return (px / gridSize) * FEET_PER_SQUARE
}

/** Render geometry + label for a circle (burst/sphere) template. */
export interface CircleTemplate {
  center: Point
  /** Radius in world pixels. */
  radius: number
  /** Radius expressed in feet (rounded to the nearest foot). */
  radiusFeet: number
  label: string
}

/**
 * CIRCLE template — origin is the CENTER, the drag endpoint sets the
 * radius (`distance(start, end)`). Models a 5e burst/sphere whose size is
 * given as a radius. Label is the radius in feet.
 */
export function computeCircleTemplate(
  start: Point,
  end: Point,
  gridSize: number,
): CircleTemplate {
  const radius = distance(start, end)
  const radiusFeet = Math.round(pixelsToFeet(radius, gridSize))
  return {
    center: { x: start.x, y: start.y },
    radius,
    radiusFeet,
    label: `${radiusFeet} ft radius`,
  }
}

/** Render geometry + label for a square (cube) template. */
export interface SquareTemplate {
  center: Point
  /** Half the side length, in world pixels. */
  halfExtent: number
  /** Full side length, in world pixels. */
  side: number
  /** The 4 axis-aligned corners: TL, TR, BR, BL. */
  corners: [Point, Point, Point, Point]
  /** Side length expressed in feet (rounded). */
  sideFeet: number
  label: string
}

/**
 * SQUARE template — origin is the CENTER; the drag sets the HALF-EXTENT.
 *
 * We use the CHEBYSHEV distance (max of |dx|, |dy|) as the half-extent so
 * the square always encloses the cursor regardless of drag direction and
 * the cursor lands on an edge midpoint, not a corner. The full side is
 * `2 * halfExtent`. This keeps the template axis-aligned (5e cubes are
 * grid-aligned) and makes "drag out from the center" feel natural.
 */
export function computeSquareTemplate(
  start: Point,
  end: Point,
  gridSize: number,
): SquareTemplate {
  const halfExtent = Math.max(Math.abs(end.x - start.x), Math.abs(end.y - start.y))
  const side = 2 * halfExtent
  const cx = start.x
  const cy = start.y
  const corners: [Point, Point, Point, Point] = [
    { x: cx - halfExtent, y: cy - halfExtent }, // TL
    { x: cx + halfExtent, y: cy - halfExtent }, // TR
    { x: cx + halfExtent, y: cy + halfExtent }, // BR
    { x: cx - halfExtent, y: cy + halfExtent }, // BL
  ]
  const sideFeet = Math.round(pixelsToFeet(side, gridSize))
  return {
    center: { x: cx, y: cy },
    halfExtent,
    side,
    corners,
    sideFeet,
    label: `${sideFeet} ft`,
  }
}

/** Render geometry + label for a cone template. */
export interface ConeTemplate {
  apex: Point
  /** Direction of the cone, radians (apex -> drag endpoint). */
  angle: number
  /** Cone length (apex to base center), in world pixels. */
  length: number
  /** The two far base corners. With apex, forms the wedge polygon. */
  baseLeft: Point
  baseRight: Point
  /** Triangle polygon points: [apex, baseLeft, baseRight]. */
  polygon: [Point, Point, Point]
  /** Length expressed in feet (rounded). */
  lengthFeet: number
  label: string
}

/**
 * CONE template — D&D 5e cone. Apex at the origin (`start`), pointed
 * toward the drag endpoint. Length L = distance(start, end). A 5e cone's
 * width at any distance from the apex equals that distance, so the base
 * width == length (the ~53.13° spread, full half-angle atan(0.5)). The
 * two far corners sit at +/- (length/2) perpendicular to the axis at the
 * base. Label is the cone length in feet.
 */
export function computeConeTemplate(
  start: Point,
  end: Point,
  gridSize: number,
): ConeTemplate {
  const length = distance(start, end)
  const angle = angleBetween(start, end)
  // Unit axis (apex -> base). Degenerate zero-length drag points +x.
  const ux = length === 0 ? 1 : (end.x - start.x) / length
  const uy = length === 0 ? 0 : (end.y - start.y) / length
  // Perpendicular unit vector.
  const px = -uy
  const py = ux
  // Base center is L along the axis; corners are +/- L/2 along the perp.
  const baseCx = start.x + ux * length
  const baseCy = start.y + uy * length
  const half = length / 2
  const baseLeft: Point = { x: baseCx + px * half, y: baseCy + py * half }
  const baseRight: Point = { x: baseCx - px * half, y: baseCy - py * half }
  const lengthFeet = Math.round(pixelsToFeet(length, gridSize))
  return {
    apex: { x: start.x, y: start.y },
    angle,
    length,
    baseLeft,
    baseRight,
    polygon: [{ x: start.x, y: start.y }, baseLeft, baseRight],
    lengthFeet,
    label: `${lengthFeet} ft cone`,
  }
}

/**
 * Check if a movement path intersects with any movement-blocking barriers
 *
 * @param from - Starting point (grid coordinates)
 * @param to - Ending point (grid coordinates)
 * @param barriers - Array of barriers to check
 * @param gridSize - Size of grid cells in pixels
 * @returns true if movement is blocked by a barrier
 */
export function isMovementBlocked(
  from: Point,
  to: Point,
  barriers: Array<{ points: Point[]; blocksMovement?: boolean }>,
  gridSize: number
): boolean {
  // Convert grid coordinates to pixel coordinates (center of cells)
  const fromPixel = {
    x: from.x * gridSize + gridSize / 2,
    y: from.y * gridSize + gridSize / 2,
  }
  const toPixel = {
    x: to.x * gridSize + gridSize / 2,
    y: to.y * gridSize + gridSize / 2,
  }

  const movementSegment: LineSegment = {
    start: fromPixel,
    end: toPixel,
  }

  // Check each barrier
  for (const barrier of barriers) {
    // Only barriers explicitly set to block movement count. Matches the toolbar
    // toggle and canvas renderer, which both treat `=== true` as blocking — a
    // freshly-drawn barrier (blocksMovement undefined) is vision-only until the
    // GM turns movement-blocking on, so it must NOT block here either.
    if (barrier.blocksMovement !== true) continue

    // Check each edge of the barrier polygon
    for (let i = 0; i < barrier.points.length; i++) {
      const p1 = barrier.points[i]
      const p2 = barrier.points[(i + 1) % barrier.points.length]

      // Convert barrier points to pixel coordinates
      const barrierSegment: LineSegment = {
        start: {
          x: p1.x * gridSize + gridSize / 2,
          y: p1.y * gridSize + gridSize / 2,
        },
        end: {
          x: p2.x * gridSize + gridSize / 2,
          y: p2.y * gridSize + gridSize / 2,
        },
      }

      // Check if movement path intersects this barrier edge
      if (lineSegmentIntersection(movementSegment, barrierSegment)) {
        return true // Movement is blocked
      }
    }
  }

  return false // Movement is not blocked
}
